import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import Location from '@r6digital/rapidstor-types/react/entities/Location';

import store from '../store';
import { selectUnitData, setUnitData } from '../store/bookingSlice';

import {
    useGetReservationQuery,
    useGetUnitListQuery,
} from '../features/rapidstor/rapidstorApiSlice';

import classes from '../routes/RapidStorPage.module.scss';

import { options } from '../services/rapidstor';

interface Props {
    location: Location;
}

function RapidStor({ location }: Props) {
    const unitData = useSelector(selectUnitData);
    const [searchParams] = useSearchParams();
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [skipUnitList, setSkipUnitList] = useState<boolean>(true);

    const {
        data: reservationData,
        isSuccess: reservationDataSuccess,
        refetch: refetchReservation,
        isLoading: reservationDataLoading,
    } = useGetReservationQuery({
        locationCode: location.locationCode,
        token: searchParams.get('token') ?? '',
    });

    const { data: unitList, isSuccess: unitListSuccess } = useGetUnitListQuery(
        {
            locationCode: location.locationCode,
            personaId: reservationData?.persona ?? '',
        },
        {
            skip: skipUnitList,
        }
    );

    useEffect(() => {
        if (reservationDataSuccess) {
            setSkipUnitList(false);
        }
    }, [reservationDataSuccess]);

    useEffect(() => {
        const populateState = async () => {
            if (unitData || !reservationDataSuccess || !unitListSuccess) {
                return;
            }

            const unitTypeId = reservationData?.unitType._id;
            const unitType = unitList?.unitTypes.find((unit) => unit._id === unitTypeId);
            const descriptor = unitList?.descriptors.find((descriptor) =>
                descriptor.unitTypes.some((unit) => unit._id === unitType?._id)
            );

            if (unitType && descriptor) {
                store.dispatch(
                    setUnitData({
                        descriptor,
                        unitType,
                        location,
                        reservation: reservationData ? true : false,
                    })
                );
            }
        };

        if (searchParams.get('token')) {
            populateState();
        }
    }, [
        location,
        reservationData,
        reservationDataSuccess,
        searchParams,
        unitData,
        unitList?.descriptors,
        unitList?.unitTypes,
        unitListSuccess,
    ]);

    useEffect(() => {
        if (
            !reservationDataLoading &&
            reservationData &&
            reservationData.ledgerResult?.esign.url
        ) {
            iframeRef.current?.remove();
            window.location.href = reservationData.ledgerResult.esign.url;
        }
    }, [reservationDataSuccess, reservationDataLoading, reservationData]);

    const handleTrackEvent = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (event: any) => {
            console.info(event);
            if (event.data && event.data.key === 'rapidstor_move_in') {
                console.info('RapidStor move in event', event.data);
                window.dispatchEvent(new CustomEvent('rapidstor_move_in', event.data));
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: 'rapidstor_move_in',
                    category: 'rapidstor',
                    detail: event.data,
                });
                iframeRef.current?.remove();
                console.info('Refetching reservation data');
                refetchReservation();
            } else {
                console.info('RapidStor event', event.data);
            }
        },
        [refetchReservation]
    );

    useEffect(() => {
        window.addEventListener('message', handleTrackEvent);

        return () => {
            window.removeEventListener('message', handleTrackEvent);
        };
    }, [handleTrackEvent]);

    useEffect(() => {
        if (!window.UStoreIt) {
            window.UStoreIt = {};
        }

        if (!window.rapidstor_api_tracking_listener) {
            window.rapidstor_api_tracking_listener = function (type, context) {
                console.info(type, context);
            };
        }

        if (!location.token) {
            return;
        }
        const token = searchParams.get('token');
        if (!token) {
            return;
        }

        const iFrame =
            (document.getElementById('rapidstor-v1-iframe') as HTMLIFrameElement) ??
            document.createElement('iframe');

        iFrame.srcdoc = `
            <!-- Google Tag Manager -->
            <script>(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','GTM-WPDZ3K3');</script>
            <!-- End Google Tag Manager -->

            <script>
            console.info(window.rapidstor_api_tracking_listener)
            if (!window.rapidstor_api_tracking_listener) {
                window.rapidstor_api_tracking_listener = function (type, context) {
                    console.info(type, context)
                    if (type === 'move-in') {
                        window.parent.postMessage({ key: 'rapidstor_move_in', context: context }, '*');
                    } else {
                        window.parent.postMessage('rapidstor_' + type, '*');
                    }
                };
                console.info(window.rapidstor_api_tracking_listener)
            }

            
            </script>

            <div id="rapidstor-container"></div>

            <style>
            
            </style>

            <script>
                const child = document.createElement('div');
                const container = document.getElementById('rapidstor-container');
                child.setAttribute('id', 'rapidstor-app');
                container?.appendChild(child);

                const o = {
                    mode: 'single-page/complete-move-in',
                    token: {
                        corp: '43776b3efe8981c279803df96f36afecf530fae882522cfd0b157822fc64fbe772a019f571060c91c2639794e7b4e72f',
                        site: '${location.token}',
                    },
                    url: '${options.baseURL.replace(/\/$/, '')}',
                    parent_element_id: 'rapidstor-app',
                    mount_element: '#rapidstor-app',
                    script_path: '/public/application/scripts/publicPanel/bootstrapper/bootstrapper.js',
                    ignore_template: false,
                    standalone: true,
                    usingV2: true,
                    load_scripts: true,
                    reservation_token: '${token}',
                };

                const s = document.createElement('script');
                const e = Math.round(new Date().getTime() / 1000);
                window.rapidstor_options = o;
                s.src = o.url + o.script_path + '?epoch=' + e;
                s.type = 'text/javascript';
                s.async = true;
                container?.appendChild(s);
            </script>
        `;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className={`${classes.container}`}>
            {unitData && (
                <div className={`${classes.descriptorCard}`}>
                    <img src={unitData.descriptor.picture} />
                    <div className={`${classes.unitData}`}>
                        <span>{unitData.descriptor.name}</span>
                        <span>
                            {unitData.unitType.width}m x {unitData.unitType.length}m
                        </span>
                        <span>{unitData.descriptor.description}</span>
                        {unitData.descriptor.descriptions && (
                            <span>{unitData.descriptor.descriptions[0]}</span>
                        )}
                    </div>
                    {unitData.unitType.deal && (
                        <div className={`${classes.dealCard}`}>
                            <span className={`${classes.title}`}>
                                {unitData.unitType.deal.title}
                            </span>
                            <div className={`${classes.rates}`}>
                                <span className={`${classes.discounted}`}>
                                    <span>
                                        $
                                        {unitData.unitType.rates.discountedRate === 0
                                            ? unitData.unitType.rates.rate
                                            : unitData.unitType.rates.discountedRate}
                                    </span>
                                    <span>/month</span>
                                </span>
                                {unitData.unitType.rates.discountedRate !== 0 && (
                                    <span className={`${classes.standard}`}>
                                        <span>${unitData.unitType.rates.rate}</span>
                                        <span>/month</span>
                                    </span>
                                )}
                            </div>
                            <div className={`${classes.descriptions}`}>
                                {unitData.unitType.deal.descriptions.map(
                                    (value, index) => {
                                        return (
                                            <span key={index}>
                                                <svg
                                                    width='15'
                                                    height='11'
                                                    viewBox='0 0 15 11'
                                                    fill='none'
                                                    xmlns='http://www.w3.org/2000/svg'
                                                >
                                                    <path
                                                        d='M1 5.5L5.33455 10L14 1'
                                                        stroke='white'
                                                        strokeWidth='1.5'
                                                        strokeLinecap='round'
                                                        strokeLinejoin='round'
                                                    />
                                                </svg>
                                                {value}
                                            </span>
                                        );
                                    }
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
            <iframe
                id='rapidstor-v1-iframe'
                className={`${classes.rapidstor}`}
                ref={iframeRef}
            />
        </div>
    );
}

export default RapidStor;
